import statAPI from '@util/api/stat';
import { formatStatistics } from '@util/formatStatistics';
import { FC, ReactNode, useEffect, useState } from 'react';
import StackedMaster, { StackedStats } from '..';
import BigPictureCta, { BigPictureCtaProps } from '../BigPictureCta';
import StackedSingle from '../StackedSingle';

export interface BigPictureCtaFields {
  fields: BigPictureCtaProps;
}

export interface BigPictureCtaSectionProps {
  bigPictureCta: any;
  statisticGroup?: any;
  customStatisticsList?: any;
  storyContentNested?: any;
}

const BigPictureCtaSection: FC<BigPictureCtaSectionProps> = ({
  bigPictureCta,
  customStatisticsList,
  statisticGroup,
  storyContentNested,
}) => {
  const [statArr, setStatArr] = useState<any[]>(
    (customStatisticsList?.[0].fields &&
      customStatisticsList?.map((stat, i) => ({
        key: i,
        statNumber: stat?.fields?.statValue,
        prepend: '',
        append: '+',
        subheader: stat?.fields?.label,
      }))) ||
      (statisticGroup?.fields?.statistics?.[0]?.fields?.defaultStatGroup?.fields?.defaultStats &&
        statisticGroup?.fields?.statistics?.[0]?.fields?.defaultStatGroup?.fields?.defaultStats?.map(
          (stat, i) => ({
            key: i,
            statNumber: stat?.fields?.value,
            prepend: '',
            append: '+',
            subheader: stat?.fields?.label,
          }),
        )) ||
      [],
  );
  const {
    header,
    eyebrow,
    richText,
    ctaText,
    ctaUrl,
    img,
    img2,
    buttonExternalOption,
    backgroundColor,
  } = bigPictureCta?.fields || {};

  const statsSelection = statisticGroup?.fields?.statistics?.[0]?.fields?.select;
  const fetchStats = async () => {
    try {
      const statAPIInstance = new statAPI();
      const statsData = await statAPIInstance.getStatsData();
      const formattedStatistics = formatStatistics(statsSelection, statsData);
      const mappedStats = formattedStatistics?.map((stat, i) => {
        return {
          key: i,
          statNumber: stat?.count,
          prepend: '',
          append: '+',
          subheader: stat?.title,
        };
      });
      setStatArr(mappedStats);
    } catch (error) {
      console.error(error);
    }
  };

  //useEffect to fetch stats
  useEffect(() => {
    if (statArr.length === 0) {
      fetchStats();
    }
  }, []);

  // Big Picture CTA shows up as existing even if all props are undefined
  // Check for header since it's a required field in contentful
  const bigPictureCtaComponent = header ? (
    <BigPictureCta
      header={header}
      eyebrow={eyebrow}
      richText={richText}
      ctaText={ctaText}
      ctaUrl={ctaUrl}
      img={img?.fields?.file?.url}
      img2={img2?.fields?.file?.url}
      buttonExternalOption={buttonExternalOption}
      backgroundColor={backgroundColor}
    />
  ) : null;

  const stackedSecondaryComponents: ReactNode[] = [];

  const stackedStats = statArr && (
    <StackedStats header={statisticGroup?.fields?.header} stats={statArr} />
  );

  if (stackedStats) {
    stackedSecondaryComponents.push(stackedStats);
  }

  //Nested Article Component
  const stackedSingle = storyContentNested && (
    <StackedSingle
      eyebrow={storyContentNested?.fields?.eyebrow}
      header={storyContentNested?.fields?.header}
      img={storyContentNested?.fields?.img?.fields?.file?.url}
      richText={storyContentNested?.fields?.bodyText}
      ctaText={storyContentNested?.fields?.ctaText}
      ctaUrl={storyContentNested?.fields?.ctaUrl}
    />
  );

  if (stackedSingle) {
    stackedSecondaryComponents.push(stackedSingle);
  }

  // Take those components created above and push them to the components array
  if (
    bigPictureCtaComponent ||
    (stackedSecondaryComponents && stackedSecondaryComponents.length > 0)
  ) {
    return (
      <StackedMaster
        mainComponent={bigPictureCtaComponent}
        secondaryComponents={stackedSecondaryComponents}
      />
    );
  }
};

export default BigPictureCtaSection;
